import { Injectable, computed, inject, signal } from '@angular/core';

import { PublicDataCacheService } from '@core/cache/public-data-cache.service';
import { StaticPageService, type StaticPageVm } from '@core/cms/static-page.service';
import { ThemeConfigService } from '@core/theme/theme-config.service';
import type { HandelProductTitleCase } from '../../theme.types';

/** Página estática del backoffice que puede sobrescribir las listas del tema. */
export const PRODUCT_TITLE_RULES_PAGE = 'titulos_de_producto';
const RULES_CACHE_KEY = `static-page:${PRODUCT_TITLE_RULES_PAGE}`;
const RULES_CACHE_TTL_MS = 60 * 60 * 1000;

const SEPARADORES_INTERNOS = /([-/'’(])(\p{L})/gu;
const NUMERO_ROMANO = /^[ivxlcdm]+$/i;

/**
 * «Pelota de Tennis» venga como venga del backend: cada palabra con inicial
 * mayúscula, los conectores en minúscula salvo al inicio, y respeto por lo que
 * el catálogo escribe a propósito (siglas, unidades, marcas como iPhone).
 */
export function formatProductTitle(raw: string, rules: HandelProductTitleCase): string {
  const texto = raw.replace(/\s+/g, ' ').trim();
  if (!texto) return texto;
  const conectores = new Set(rules.lowercaseWords.map((w) => w.toLocaleLowerCase('es')));
  const fijas = new Map(rules.keepAsIs.map((w) => [w.toLocaleLowerCase('es'), w]));
  // Si el backend ya distingue mayúsculas, lo que escribió con intención se
  // conserva; si vino gritado o todo en minúscula, no hay intención que leer.
  const traeIntencion =
    texto !== texto.toLocaleUpperCase('es') && texto !== texto.toLocaleLowerCase('es');

  return texto
    .split(' ')
    .map((palabra, indice) => {
      const clave = palabra.toLocaleLowerCase('es');
      const fija = fijas.get(clave);
      if (fija) return fija;
      if (/\d/.test(palabra)) return palabra;
      if (traeIntencion && conservaSuForma(palabra)) return palabra;
      if (indice > 0 && conectores.has(clave)) return clave;
      return capitalizar(clave);
    })
    .join(' ');
}

/** «iPhone», «LG», «USB-C», «II»: forma deliberada del catálogo. */
function conservaSuForma(palabra: string): boolean {
  const letras = palabra.replace(/[^\p{L}]/gu, '');
  if (!letras) return false;
  const esSigla = letras === letras.toLocaleUpperCase('es') && letras.length <= 4;
  const mayusculaInterna = /\p{Ll}\p{Lu}/u.test(palabra);
  const romano = NUMERO_ROMANO.test(letras) && letras === letras.toLocaleUpperCase('es');
  return esSigla || mayusculaInterna || romano;
}

function capitalizar(palabra: string): string {
  const inicial = palabra.replace(/^(\p{L})/u, (letra) => letra.toLocaleUpperCase('es'));
  return inicial.replace(
    SEPARADORES_INTERNOS,
    (_, sep: string, letra: string) => sep + letra.toLocaleUpperCase('es'),
  );
}

/**
 * Las listas viven en el tema como valor por defecto y en el CMS como valor
 * vivo: negocio agrega una sigla o un conector desde el backoffice, sin deploy.
 * En la página `titulos_de_producto` valen dos campos, `conectores` y `siglas`,
 * con las palabras separadas por coma o salto de línea.
 */
@Injectable({ providedIn: 'root' })
export class ProductTitleService {
  private readonly theme = inject(ThemeConfigService);
  private readonly pages = inject(StaticPageService);
  private readonly cache = inject(PublicDataCacheService);
  private readonly fromCms = signal<Partial<HandelProductTitleCase>>({});
  private readonly rules = computed<HandelProductTitleCase>(() => ({
    ...this.theme.config().productTitleCase,
    ...this.fromCms(),
  }));

  format(title: string): string {
    return formatProductTitle(title, this.rules());
  }

  /** Al arrancar: sin página en el CMS, o con la API caída, siguen las del tema. */
  async cargarDesdeCms(): Promise<void> {
    try {
      const page = await this.cache.get(RULES_CACHE_KEY, RULES_CACHE_TTL_MS, () =>
        this.pages.getPage(PRODUCT_TITLE_RULES_PAGE),
      );
      this.fromCms.set(rulesFromPage(page));
    } catch {
      this.fromCms.set({});
    }
  }
}

export function rulesFromPage(page: StaticPageVm): Partial<HandelProductTitleCase> {
  const campo = (key: string): readonly string[] | undefined => {
    const value = page.sections
      .flatMap((section) => section.content)
      .find((item) => item.key === key)?.value;
    if (typeof value !== 'string') return undefined;
    const palabras = value
      .split(/[,\n;]/)
      .map((w) => w.trim())
      .filter(Boolean);
    return palabras.length ? palabras : undefined;
  };
  const conectores = campo('conectores');
  const siglas = campo('siglas');
  return {
    ...(conectores ? { lowercaseWords: conectores } : {}),
    ...(siglas ? { keepAsIs: siglas } : {}),
  };
}
